import React, { useState, useEffect } from 'react';
import NetworkApi from '../services/networks';
import UserApi from '../services/users';

const AddToNetworkModal = ({ isOpen, onClose, user, token, onSuccess }) => {
    const [networks, setNetworks] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selectedNetwork, setSelectedNetwork] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            fetchNetworks();
            setSelectedNetwork('');
            setSelectedRole('');
            setError('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (selectedNetwork) {
            fetchRoles(selectedNetwork);
        } else {
            setRoles([]);
            setSelectedRole('');
        }
    }, [selectedNetwork]);

    const fetchNetworks = async () => {
        try {
            const networkApi = new NetworkApi(token);
            const response = await networkApi.all("Published", 1);
            setNetworks(response.networks || []);
        } catch (error) {
            console.error('Error fetching networks:', error);
            setError('Failed to load networks');
        }
    };

    const fetchRoles = async (networkId) => {
        try {
            const userApi = new UserApi(token);
            const response = await userApi.getRoles(networkId);
            const filteredRoles = response.filter(role => 
                role.name === 'Admin' || role.name === 'Owner' || role.name === 'Member'
            );
            setRoles(filteredRoles);
        } catch (error) {
            console.error('Error fetching roles:', error);
            setError('Failed to load roles for selected network');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedNetwork || !selectedRole) {
            setError('Please select both a network and a role');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const userApi = new UserApi(token);
            await userApi.addMember(selectedNetwork, user.id, selectedRole);
            
            // Close modal immediately and let parent handle success message
            onSuccess(`User "${user.first_name} ${user.last_name}" has been added to the network successfully`);
            onClose();
            
        } catch (error) {
            console.error('Error adding user to network:', error);
            setError(error.message || 'Failed to add user to network');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedNetwork('');
        setSelectedRole('');
        setError('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Add User to Network</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={handleClose}
                            disabled={loading}
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {user && (
                                <div className="mb-3">
                                    <p><strong>User:</strong> {user.first_name} {user.last_name}</p>
                                    <p><strong>Email:</strong> {user.email}</p>
                                </div>
                            )}
                            
                            {error && (
                                <div className="alert alert-danger">
                                    {error}
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="networkSelect" className="form-label">Select Network</label>
                                <select
                                    id="networkSelect"
                                    className="form-select"
                                    value={selectedNetwork}
                                    onChange={(e) => setSelectedNetwork(e.target.value)}
                                    required
                                    disabled={loading}
                                >
                                    <option value="">Choose a network...</option>
                                    {networks.map(network => (
                                        <option key={network.id} value={network.id}>
                                            {network.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mb-3">
                                <label htmlFor="roleSelect" className="form-label">Select Role</label>
                                <select
                                    id="roleSelect"
                                    className="form-select"
                                    value={selectedRole}
                                    onChange={(e) => setSelectedRole(e.target.value)}
                                    required
                                    disabled={loading || !selectedNetwork}
                                >
                                    <option value="">Choose a role...</option>
                                    {roles.map(role => (
                                        <option key={role.id} value={role.id}>
                                            {role.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={handleClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={loading || !selectedNetwork || !selectedRole}
                            >
                                {loading ? 'Adding...' : 'Add to Network'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddToNetworkModal;
